import { useCallback, useEffect, useRef, useState } from 'react';
import { editCaseStyles } from './stylesheet';
import { casesApi } from 'src/api/casesApi';
import toast from 'react-hot-toast';
import { userApi } from 'src/api/userApi';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Mention,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { projectApi } from 'src/api/projectApi';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  List as MuiList,
  Typography,
} from '@material-ui/core';
import FileUploadDragDrop from 'src/components/widgets/fileUpload/FileUploadDragDrop';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CancelIcon from '@material-ui/icons/Cancel';
import CustomButton from 'src/components/widgets/buttons/CustomButton';
import { UserActivity } from 'src/components/widgets/display-blocks/user-activity';
import parseHTML from 'html-react-parser';
import { Skeleton } from '@mui/material';
import logger from 'src/utils/logger';

const useStyles = editCaseStyles;

type CommentLinksTypes = 'none' | 'external' | 'internal';

export const CasesComments = ({
  toggleIsLoad,
  caseData,
  user,
  languageType,
  showTranslation,
  handleDocumentClick,
  handleImageClick,
}: any) => {
  const classes = useStyles();
  const commentRef = useRef(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [commentValue, setCommentValue] = useState<string>('');
  const [commentEdit, setCommentEdit] = useState<any>({});
  const [filePath, setFilePath] = useState<string[]>([]);
  const [commentType, setCommentType] = useState<CommentLinksTypes>('none');
  const [isExternalComment, setIsExternalComment] = useState<boolean>(false);
  const [commentDeleteId, setCommentDeleteId] = useState<string>('');
  const [isDeclineCase, setIsDeclineCase] = useState<boolean>(false);
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionUsers, setMentionUsers] = useState([]); // Store fetched mention users
  const [externalUsers, setExternalUsers] = useState({});
  const [isLoadingMentions, setIsLoadingMentions] = useState<boolean>(false);

  useEffect(() => {
    if (window.location.href.includes('declined-cases')) {
      setIsDeclineCase(true);
    }
  }, []);
  const handleCancelComment = () => {
    setCommentType('none');
    setCommentValue('');
    setIsEditing(false);
  };

  const isNotEmpty = (val: string | any[]) => {
    if (Array.isArray(val)) {
      return val.length > 0;
    }
    return typeof val === 'string' && val.trim() !== '';
  };

  const getExternalUsers = (users) =>
    users.reduce((acc, u) => {
      acc[`@${u.name}`] = u.isExternal;
      return acc;
    }, {});

  // Fetch the users dynamically based on `isExternalComment`
  const fetchEmployees = async (isExternal: boolean) => {
    try {
      setIsLoadingMentions(true); // Set loading state
      let response;
      if (isExternal) {
        response = await projectApi.getUsersByProj({
          organizationId: caseData?.organization?.id,
        });
      } else {
        response = await projectApi.getInternalUsersByProj();
      }
      const users = response?.data?.map((mentionUser) => ({
        id: `@${mentionUser.name}`,
        name: mentionUser.name,
        userId: mentionUser.id,
        isExternal: mentionUser.isExternal, // Add this property for class differentiation
      }));

      setExternalUsers(getExternalUsers(users));

      setMentionUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingMentions(false); // End loading state
    }
  };

  const handleSaveComment = async (e) => {
    e.preventDefault();
    try {
      if (isNotEmpty(commentValue) || isNotEmpty(filePath)) {
        const payload = {
          caseId: caseData?.id,
          description: commentValue,
          isExternal: isExternalComment,
          attachmentPath: filePath,
          taggedUsers: mentionedUserIds,
        };
        const response = await casesApi?.createComment(payload);
        if (response?.status === 201) {
          toast.success('Comment Added');
          setCommentValue('');
          setFilePath([]);
          setCommentType('none');
          toggleIsLoad();
        }
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message);
      }
    }
  };
  const deleteComment = async (caseId: String) => {
    try {
      const response = await casesApi?.deleteComments(caseId, commentDeleteId);
      if (response?.status === 200) {
        toast.success('Comment Deleted');
        toggleIsLoad();
        setOpenDialog(false);
        toggleIsLoad();
        if (isEditing) {
          setIsEditing(false);
          setCommentValue('');
          setCommentEdit({
            commentId: '',
          });
        }
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message);
      }
    }
  };
  const editCommentAPI = async (e) => {
    e.preventDefault();
    try {
      if (isNotEmpty(commentValue)) {
        const payload = {
          caseId: commentEdit?.caseId,
          description: commentValue,
          attachmentPath: filePath,
          taggedUsers: mentionedUserIds,
        };
        const response = await casesApi?.editCaseComment(
          commentEdit?.commentID,
          payload
        );
        if (response?.status === 200) {
          toast.success('Comment Updated');
          toggleIsLoad();
          setCommentValue('');
          setCommentType('none');
          setIsEditing(false);
        }
      }
    } catch (error) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message);
      }
    }
  };
  const editComment = async (
    caseIDs: string,
    commentId: string,
    commentDes: string,
    indexMap: Number,
    filePaths: string[]
  ) => {
    setIsEditing(true);
    setFilePath(filePaths);
    setCommentValue(commentDes);
    setCommentEdit({
      caseId: caseIDs,
      commentID: commentId,
      currentIndex: indexMap,
    });
  };
  const handleEditCommentDeleteFile = (e, index) => {
    e.stopPropagation();
    const updatedFiles = [...filePath];
    updatedFiles.splice(index, 1);
    setFilePath(updatedFiles);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleClickInternalComment = () => {
    setCommentType('internal');
    setIsExternalComment(false);
    setMentionUsers([]); // Clear mention users immediately when switching to internal
    fetchEmployees(false); // Fetch internal users
  };

  const handleClickExternalComment = () => {
    setCommentType('external');
    setIsExternalComment(true);
    setMentionUsers([]); // Clear mention users immediately when switching to external
    fetchEmployees(true); // Fetch external users
  };

  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise((resolve) => {
          loader.file.then(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const image = await userApi.uploadAttachments(formData);
            resolve({
              default: `${image?.data?.blob_url}`,
            });
          });
        }),
    };
  }
  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader);
  }

  const modifyImgAttributes = (node) => {
    if (node.name === 'img') {
      // Check if src attribute is present
      if (
        node.attribs &&
        node.attribs.src &&
        node.attribs.src.includes('backupdbaccount.blob.core.windows.net')
      ) {
        // Modify the src attribute as per your requirement
        // const splittedUrl = node.attribs.src?.split('?');
        // splittedUrl[1] =
        //   'sp=racwdl&st=2024-02-27T13:21:58Z&se=2030-07-29T21:21:58Z&spr=https&sv=2022-11-02&sr=c&sig=YXOHT7%2Bx8VW3RDdgzehtPZLWJ6zHJz%2Bp3QtnduafL4o%3D';
        // node.attribs.src = splittedUrl?.join('?');
      }
    }
    return node;
  };
  const editorConfig = {
    toolbar: {
      items: [
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'blockQuote',
        'insertImage',
        '|',
        'bulletedList',
        'numberedList',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AutoImage,
      BlockQuote,
      Bold,
      Essentials,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      Paragraph,
      SimpleUploadAdapter,
      TextTransformation,
      Mention,
    ],
    blockToolbar: [
      'bold',
      'italic',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    mention: {
      feeds: [
        {
          marker: '@',
          feed: (query) => {
            // Filter mention users based on query
            if (query.length === 0) {
              return mentionUsers; // Return all users when no query
            }
            return mentionUsers.filter((mention) =>
              mention.name.toLowerCase().includes(query.toLowerCase())
            );
          },
          minimumCharacters: 0,
        },
      ],
    },
    extraPlugins: [uploadPlugin],
    placeholder: 'Enter your comment here',
  };

  const onReady = useCallback(
    (editor) => {
      // Downcast for editing (live editing in the editor)
      editor.conversion.for('editingDowncast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
          if (!modelAttributeValue) return;

          const mentionId = modelAttributeValue.id;

          // Check if the user is external
          const isExternal = externalUsers[mentionId] || false;
          const userType = isExternal ? 'mention-external' : 'mention-internal';

          const mentionElement = writer.createAttributeElement('span', {
            class: `mention ${userType}`,
            'data-mention': mentionId,
          });

          // Return the mention element
          // eslint-disable-next-line consistent-return
          return mentionElement;
        },
        converterPriority: 'high',
      });

      // Downcast for data (when saving content)
      editor.conversion.for('dataDowncast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
          if (!modelAttributeValue) return;

          const mentionId = modelAttributeValue.id;

          // Check if the user is external
          const isExternal = externalUsers[mentionId] || false;
          const userType = isExternal ? 'mention-external' : 'mention-internal';

          const mentionElement = writer.createAttributeElement('span', {
            class: `mention ${userType}`,
            'data-mention': mentionId,
          });

          // Return the mention element
          // eslint-disable-next-line consistent-return
          return mentionElement;
        },
        converterPriority: 'high',
      });

      // Reapply mention styles for existing content
      editor.setData(editor.getData());
    },
    [isEditing, externalUsers] // Ensure the onReady callback reinitializes when isEditing changes
  );

  return (
    <Box
      sx={{
        padding: '10px 0 12px !important',
      }}
      className={`${classes.box} ${isDeclineCase ? classes.pointerEvent : ''}`}>
      {!isDeclineCase && commentType === 'none' && (
        <Box className={classes.commentOptionButtons}>
          <Box
            className="internalCommentLink"
            onClick={handleClickInternalComment}>
            Add Internal Comment
          </Box>{' '}
          |
          <Box
            className="externalCommentLink"
            onClick={handleClickExternalComment}>
            Add External Comment
          </Box>
        </Box>
      )}
      {commentType !== 'none' && (
        <div
          style={{ flexGrow: 1, marginBottom: 16 }}
          data-color-mode="light"
          className="editorMarkup"
          ref={commentRef}>
          <div className={classes.commentEditorContainer}>
            {isLoadingMentions ? (
              <Skeleton height={100} />
            ) : (
              <CKEditor
                editor={ClassicEditor}
                data={commentValue}
                onReady={onReady}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setCommentValue(data);

                  // Clear the existing mentions array
                  const newMentionedUserIds = [];

                  // Access the editor model
                  const root = editor.model.document.getRoot();

                  // Traverse the editor content to find mention attributes
                  Array.from(root.getChildren())
                    .flatMap((block: any) => Array.from(block.getChildren()))
                    .filter(
                      (item: any) =>
                        item.hasAttribute && item.hasAttribute('mention')
                    )
                    .forEach((mention: any) => {
                      const mentionUserId =
                        mention.getAttribute('mention').userId;
                      if (
                        mentionUserId &&
                        !newMentionedUserIds.includes(mentionUserId)
                      ) {
                        newMentionedUserIds.push(mentionUserId);
                      }
                    });

                  // Update the state with the new array of mentioned user IDs
                  setMentionedUserIds(newMentionedUserIds);
                }}
                config={editorConfig as any}
              />
            )}
            <FileUploadDragDrop setFilePath={setFilePath} filePath={filePath} />
            {isEditing && (
              <div className="editCommentFileParent">
                {isEditing &&
                  filePath?.map((item, index) => {
                    // const splittedUrl = item?.split('?');
                    // splittedUrl[1] =
                    //   'sp=racwdl&st=2024-02-27T13:21:58Z&se=2030-07-29T21:21:58Z&spr=https&sv=2022-11-02&sr=c&sig=YXOHT7%2Bx8VW3RDdgzehtPZLWJ6zHJz%2Bp3QtnduafL4o%3D';
                    // item = splittedUrl?.join('?');
                    const getFileType = (items) => {
                      if (items.includes('.pdf')) {
                        return 'PDF';
                      }
                      if (items.includes('.doc')) {
                        return 'DOC';
                      }
                      if (items.includes('.docx')) {
                        return 'DOCX';
                      }
                      if (items.includes('.txt')) {
                        return 'TXT';
                      }
                      return null;
                    };

                    const fileType = getFileType(item);
                    if (
                      item.includes('.pdf') ||
                      item.includes('.doc') ||
                      item.includes('.docx') ||
                      item.includes('.txt')
                    ) {
                      return (
                        <a
                          role="presentation"
                          onClick={() => handleDocumentClick(item)}>
                          {' '}
                          <span>{fileType}</span>
                          <InsertDriveFileIcon sx={{ fontSize: '60px' }} />
                          <CancelIcon
                            onClick={(e) =>
                              handleEditCommentDeleteFile(e, index)
                            }
                            className="editCommentFileEdit"
                          />
                        </a>
                      );
                    }
                    const imageFormats = [
                      '.jpg',
                      '.jpeg',
                      '.png',
                      '.gif',
                      '.bmp',
                      '.tiff',
                      '.webp',
                      '.svg',
                      '.ico',
                    ];
                    if (
                      imageFormats.some((extension) =>
                        item?.includes(extension)
                      )
                    ) {
                      return (
                        <>
                          <a
                            role="presentation"
                            onClick={() => handleImageClick(item)}>
                            <img src={item} alt="img" />
                            <CancelIcon
                              onClick={(e) =>
                                handleEditCommentDeleteFile(e, index)
                              }
                              className="editCommentFileEdit"
                            />
                          </a>
                        </>
                      );
                    }
                    return null;
                  })}
              </div>
            )}
          </div>
          <div className="btnComments">
            {(user?.permissions?.includes('case-comment-update') ||
              user?.permissions?.includes('case-comment-create')) &&
              (!isEditing ? (
                <CustomButton
                  value={
                    isExternalComment
                      ? 'Add External Comment'
                      : 'Add Internal Comment'
                  }
                  customClass={classes.customClassAddComment}
                  color="secondary"
                  onClick={handleSaveComment}
                />
              ) : (
                user?.permissions?.includes('case-comment-update') && (
                  <>
                    <CustomButton
                      value="Update Comment"
                      color="secondary"
                      onClick={(e) => editCommentAPI(e)}
                    />

                    <CustomButton
                      value="Cancel"
                      color="text"
                      onClick={handleCancelComment}
                    />
                  </>
                )
              ))}
            {!isEditing && (
              <CustomButton
                value="Cancel"
                color="text"
                onClick={handleCancelComment}
              />
            )}
          </div>
        </div>
      )}
      {caseData?.comment?.length !== 0 && (
        <>
          <Typography
            variant="body2"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              margin: '0px 0px 8px 0px',
            }}>
            Comments
          </Typography>
          <MuiList className={classes.commentsPreview}>
            {caseData?.comment?.map((comment, i) => {
              function extractTextFromHTML(htmlString) {
                // Create a new DOMParser
                const parser = new DOMParser();

                // Parse the HTML string
                const doc = parser.parseFromString(htmlString, 'text/html');

                // Get the text content from the parsed HTML
                const { textContent } = doc.body;

                return textContent;
              }
              const extractedText = extractTextFromHTML(comment?.description);
              return (
                <UserActivity
                  isExternalComment={comment?.isExternal}
                  customClasses={`${
                    isEditing && commentEdit?.currentIndex === i
                      ? 'current'
                      : ''
                  }
                ${comment?.isExternal && classes.externalComment} `}
                  meta={{
                    description: '',
                    createdBy: comment?.createdByName,
                    createdAt: comment?.createdAt,
                    isExternal: comment?.isExternal || false,
                  }}
                  details={parseHTML(comment?.description, {
                    replace: modifyImgAttributes,
                  })}
                  filesPath={comment?.attachmentPath}
                  actions={[
                    // <Button
                    //   size="small"
                    //   title="Translate"
                    //   type="button"
                    //   onClick={(e) => {
                    //     e.preventDefault();
                    //     e.stopPropagation();
                    //     showTranslation(
                    //       extractedText,
                    //       languageType,
                    //       'caseCommentData',
                    //       comment?.id
                    //     );
                    //   }}
                    //   sx={{ padding: '0' }}>
                    //   Translate
                    // </Button>,
                    <Button
                      size="small"
                      title="Edit comment"
                      type="button"
                      onClick={() => {
                        editComment(
                          caseData?.id,
                          comment?.id,
                          comment?.description,
                          i,
                          comment?.attachmentPath
                        );
                        setCommentType(
                          comment?.isExternal ? 'external' : 'internal'
                        );
                        setMentionUsers([]);
                        fetchEmployees(!!comment?.isExternal);
                        // commentRef.current?.scrollIntoView({ behaviour: 'smooth' });
                      }}
                      sx={{ padding: '0' }}>
                      Edit
                    </Button>,
                    <Button
                      size="small"
                      title="Delete comment"
                      type="button"
                      onClick={() => {
                        setOpenDialog(true);
                        setCommentDeleteId(comment?.id);
                      }}>
                      Delete
                    </Button>,
                    <Dialog
                      open={openDialog}
                      onClose={handleCloseDialog}
                      aria-labelledby="alert-dialog-title"
                      aria-describedby="alert-dialog-description">
                      <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                          Are you sure, you want to delete a comment?
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleCloseDialog} color="primary">
                          Cancel
                        </Button>
                        <Button
                          onClick={() => deleteComment(caseData?.id)}
                          color="primary"
                          autoFocus>
                          Yes
                        </Button>
                      </DialogActions>
                    </Dialog>,
                  ]}
                />
              );
            })}
          </MuiList>
        </>
      )}
    </Box>
  );
}; // ----> Comments()
